// src/pages/Grow/Intakes/Components/FacilitatorPicker.tsx
/**
 * Picks the facilitators an intake will be assigned to on approval.
 *
 * The Grow counterpart of `Counselling/Requests/Components/LeaderPicker`, with two real differences
 * that come straight from `IntakeApproveRequest`:
 *
 * 1. **Several facilitators, not one.** `facilitatorUserIds?: string[]`, so this is a multi-select.
 * 2. **One of them may be the lead.** `leadFacilitatorUserId?: string`, chosen from the selected set.
 *
 * ⚠️ The scope control is the point, exactly as on the leader picker: `courseService.getFacilitators(
 * courseID)` returns the facilitators already covering **this course**, so the drawer opens on the right
 * shortlist rather than on everybody. "All facilitators" falls back to `facilitatorService.list()`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Avatar, Badge, Button, Card, Group, ScrollArea, SegmentedControl, Stack, Text,
} from '@mantine/core';
import { IconSearch, IconStar, IconStarFilled } from '@tabler/icons-react';
import { TextInput } from '@/components';
import {
  useACCourseService, useFacilitatorService,
  type CourseFacilitatorView, type FacilitatorModelView,
} from '@actschurch/ccms-sdk';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { resolveAvatarSrc, getInitialsFromName } from '@/utils/avatar';

export type FacilitatorScope = 'course' | 'all';

/**
 * The fields both facilitator shapes share.
 *
 * ⚠️ `CourseFacilitatorView` and `FacilitatorModelView` are different types, but they agree on every
 * field this picker reads. Normalising to one local shape keeps the card markup single-sourced instead
 * of branching on the scope.
 */
interface PickableFacilitator {
  userId: string;
  name: string;
  contact: string;
  avatar?: string | null;
  statusName?: string | null;
  /** Only `FacilitatorModelView` carries a load figure, so this is absent on the course scope. */
  activeAssignments?: number;
}

function facilitatorName(f: { firstName?: string | null; surname?: string | null; fullName?: string | null; displayName?: string; userId: string }): string {
  return [f.firstName, f.surname].filter(Boolean).join(' ').trim()
    || f.fullName?.trim()
    || f.displayName?.trim()
    || f.userId;
}

const fromCourse = (f: CourseFacilitatorView): PickableFacilitator => ({
  userId: f.userId,
  name: facilitatorName(f),
  contact: f.email || f.phone || 'No contact',
  avatar: f.avatar,
  statusName: f.statusName,
});

const fromAll = (f: FacilitatorModelView): PickableFacilitator => ({
  userId: f.userId,
  name: facilitatorName(f),
  contact: f.email || f.phone || 'No contact',
  avatar: f.avatar,
  statusName: f.statusName,
  activeAssignments: f.activeAssignmentsCount,
});

export interface FacilitatorPickerProps {
  /** The intake's course, used to open on the facilitators who already cover it. */
  courseID: number;
  /** Selected facilitator user IDs. */
  value: string[];
  onChange: (userIds: string[]) => void;
  /** The designated lead, always one of `value`. */
  leadUserId: string | null;
  onLeadChange: (userId: string | null) => void;
}

export default function FacilitatorPicker({
  courseID, value, onChange, leadUserId, onLeadChange,
}: FacilitatorPickerProps) {
  const courseService = useACCourseService();
  const facilitatorService = useFacilitatorService();

  const [scope, setScope] = useState<FacilitatorScope>('course');
  const [rows, setRows] = useState<PickableFacilitator[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  /**
   * Everyone ever loaded, keyed by user ID.
   *
   * ⚠️ Needed because the selected chips must keep their names when the scope narrows or the search
   * changes. Without it, selecting someone under "All facilitators" and switching back to the course
   * scope would leave a chip reading as a raw GUID.
   */
  const [known, setKnown] = useState<Record<string, PickableFacilitator>>({});

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const term = debouncedSearch.trim() || undefined;
      const next = scope === 'course'
        ? (await courseService.getFacilitators(courseID) ?? []).map(fromCourse)
        : (await facilitatorService.list(undefined, term) ?? []).map(fromAll);
      setRows(next);
      setKnown((prev) => {
        const merged = { ...prev };
        next.forEach((f) => { merged[f.userId] = f; });
        return merged;
      });
    } catch (e) {
      console.error('[FacilitatorPicker] failed to load facilitators:', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [courseService, facilitatorService, scope, courseID, debouncedSearch]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  /**
   * ⚠️ The search is re-applied locally on both scopes. `getFacilitators` takes no search term at all,
   * and a backend that ignored `search` on `list` would otherwise make the box look broken.
   */
  const visible = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    return rows
      .filter((f) => !term || [f.name, f.contact].some((v) => v.toLowerCase().includes(term)))
      .sort((a, b) => (a.activeAssignments ?? 0) - (b.activeAssignments ?? 0));
  }, [rows, debouncedSearch]);

  const toggle = (userId: string) => {
    const isSelected = value.includes(userId);
    const next = isSelected ? value.filter((id) => id !== userId) : [...value, userId];
    onChange(next);
    // Deselecting the lead must clear the lead, otherwise approve sends a lead who is not assigned.
    if (isSelected && leadUserId === userId) onLeadChange(null);
    // First pick becomes the lead by default; the admin can move the star.
    if (!isSelected && next.length === 1) onLeadChange(userId);
  };

  const selected = value.map((id) => known[id]).filter(Boolean);

  return (
    <Stack gap="sm">
      <SegmentedControl
        value={scope}
        onChange={(v) => setScope(v as FacilitatorScope)}
        fullWidth
        size="xs"
        data={[
          { value: 'course', label: 'Covers this course' },
          { value: 'all', label: 'All facilitators' },
        ]}
      />

      <TextInput
        placeholder="Search facilitators by name, email or phone…"
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        leftSection={<IconSearch size={16} />}
        radius="md"
        size="sm"
      />

      {selected.length > 0 && (
        <Card withBorder radius="md" padding="sm" style={{ borderColor: 'var(--mantine-primary-color-filled)' }}>
          <Stack gap="xs">
            <Group justify="space-between" wrap="nowrap">
              <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                {selected.length} selected
              </Text>
              <Button
                size="compact-xs"
                variant="subtle"
                onClick={() => { onChange([]); onLeadChange(null); }}
              >
                Clear all
              </Button>
            </Group>
            {selected.map((f) => (
              <Group key={f.userId} gap="sm" justify="space-between" wrap="nowrap">
                <Text size="sm" fw={600} truncate style={{ minWidth: 0 }}>{f.name}</Text>
                <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                  <Button
                    size="compact-xs"
                    variant={leadUserId === f.userId ? 'filled' : 'subtle'}
                    color="yellow"
                    leftSection={leadUserId === f.userId ? <IconStarFilled size={12} /> : <IconStar size={12} />}
                    onClick={() => onLeadChange(leadUserId === f.userId ? null : f.userId)}
                  >
                    {leadUserId === f.userId ? 'Lead' : 'Make lead'}
                  </Button>
                  <Button size="compact-xs" variant="subtle" color="red" onClick={() => toggle(f.userId)}>
                    Remove
                  </Button>
                </Group>
              </Group>
            ))}
          </Stack>
        </Card>
      )}

      {loading ? (
        <Text size="sm" c="dimmed" ta="center" py="md">Loading facilitators…</Text>
      ) : visible.length === 0 ? (
        <Stack gap="xs" align="center" py="md">
          <Text size="sm" c="dimmed" ta="center">
            {scope === 'course'
              ? 'No facilitator is assigned to this course yet.'
              : 'No facilitators found.'}
          </Text>
          {/* Widen explicitly rather than silently, so an empty shortlist is visibly a fact. */}
          {scope === 'course' && (
            <Button size="compact-sm" variant="light" onClick={() => setScope('all')}>
              Widen to all facilitators
            </Button>
          )}
        </Stack>
      ) : (
        <ScrollArea.Autosize mah={260}>
          <Stack gap="xs">
            {visible.map((f) => {
              const isSelected = value.includes(f.userId);
              return (
                <Card
                  key={f.userId}
                  withBorder
                  radius="md"
                  padding="sm"
                  role="button"
                  tabIndex={0}
                  style={{
                    cursor: 'pointer',
                    borderColor: isSelected ? 'var(--mantine-primary-color-filled)' : undefined,
                  }}
                  onClick={() => toggle(f.userId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(f.userId); }
                  }}
                >
                  <Group gap="sm" wrap="nowrap" justify="space-between">
                    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                      <Avatar src={resolveAvatarSrc(f.avatar)} alt={f.name} radius="xl" size="sm">
                        {getInitialsFromName(f.name)}
                      </Avatar>
                      <div style={{ minWidth: 0 }}>
                        <Text size="sm" fw={600} truncate>{f.name}</Text>
                        <Text size="xs" c="dimmed" truncate>{f.contact}</Text>
                      </div>
                    </Group>
                    <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                      {f.statusName && <Badge size="xs" variant="light">{f.statusName}</Badge>}
                      {f.activeAssignments !== undefined && (
                        <Badge size="xs" variant="light" color={f.activeAssignments === 0 ? 'green' : 'gray'}>
                          {f.activeAssignments} active
                        </Badge>
                      )}
                      {leadUserId === f.userId && (
                        <Badge size="xs" variant="filled" color="yellow">Lead</Badge>
                      )}
                    </Group>
                  </Group>
                </Card>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>
      )}

      {!loading && visible.length > 0 && (
        <Text size="xs" c="dimmed">
          {visible.length} facilitator{visible.length === 1 ? '' : 's'}
          {scope === 'all' ? ', least busy first.' : ' covering this course.'}
        </Text>
      )}
    </Stack>
  );
}
